import axios, { AxiosResponse } from "axios";
import { BASE_URL } from "./constants";
import { store } from "../redux/store";
import { replaceNullWithEmptyString, resetStore } from "./helper";

// Types
interface QueryParams {
  [key: string]: string | number | boolean;
}

interface RequestHeaders {
  "Content-Type": string;
  "x-authorization"?: string;
  [key: string]: string | undefined;
}

interface ApiResponse<T = any> {
  data?: T;
  status?: number;
  message?: string;
}

interface TokenResponse {
  token: string;
  refreshToken: string;
}

type HttpMethod =
  | typeof axios.get
  | typeof axios.post
  | typeof axios.put
  | typeof axios.delete;

const storeData = store.getState();

const encodeQueryString = (params: QueryParams): string => {
  const keys = Object.keys(params);
  const queryString = keys
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  return keys.length ? `?${queryString}` : "";
};

// const getStoreData = () => {
//     console.log(storeData,"accessToken");
//     return accessToken;
// };

const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem("refreshToken");

  try {
    const response = await axios.post<TokenResponse>(`${BASE_URL}auth/token`, {
      refreshToken,
    });

    const newAccessToken = response.data?.token;

    if (newAccessToken) {
      localStorage.setItem("authToken", newAccessToken);
      localStorage.setItem("refreshToken", response.data?.refreshToken);
      return newAccessToken;
    } else {
      throw new Error("Failed to refresh access token.");
    }
  } catch (error) {
    console.error(error, "---refresh token error---");
    resetStore();
    throw error;
  }
};

const handleRequest = async <T = any>(
  method: HttpMethod,
  URL: string,
  body: any = {},
  sendResponse?: boolean
): Promise<T | AxiosResponse<T>> => {
  // const accessToken = await getStoreData();
  const accessToken = localStorage.getItem("authToken");

  const headers: RequestHeaders = {
    "Content-Type": "application/json",
  };

  try {
    if (accessToken) {
      headers["x-authorization"] = `Bearer ${accessToken}`;
    }

    const response = await method(
      BASE_URL + URL,
      method === axios.get ? { headers } : body,
      { headers }
    );

    const isUnauthorize = response.data?.status == 401;

    if (isUnauthorize) {
      try {
        const newAccessToken = await refreshAuthToken();

        // Retry original request with new token
        headers["x-authorization"] = `Bearer ${newAccessToken}`;

        const retryResponse = await method(
          BASE_URL + URL,
          method === axios.get ? { headers } : body,
          { headers }
        );

        return sendResponse ? retryResponse : retryResponse?.data;
      } catch (refreshError) {
        // Refresh token failed
        resetStore();

        if (sendResponse) {
          return response;
        } else {
          return response?.data;
        }
      }
    }

    if (sendResponse) {
      return response;
    } else {
      return response?.data;
    }
  } catch (error: unknown) {
    const axiosError = error as any;
    if (axiosError.response?.status === 401) {
      // Attempt to refresh token
      try {
        const newAccessToken = await refreshAuthToken();

        // Retry original request with new token
        headers["x-authorization"] = `Bearer ${newAccessToken}`;

        const retryResponse = await method(
          BASE_URL + URL,
          method === axios.get ? { headers } : body,
          { headers }
        );

        return sendResponse ? retryResponse : retryResponse?.data;
      } catch (refreshError) {
        resetStore();

        throw error;
      }
    }

    throw error;
  }
};

// export const fetchGETRequest = async <T = any>(
//   URL: string,
//   body: any = {},
//   sendResponse?: boolean
// ): Promise<T | AxiosResponse<T>> => {
//   return handleRequest<T>(axios.get, URL, {}, sendResponse);
// };

// Overload signatures
export async function fetchGETRequest<T = any>(
  URL: string,
  body?: any,
  sendResponse?: false
): Promise<T>;

export async function fetchGETRequest<T = any>(
  URL: string,
  body: any,
  sendResponse: true
): Promise<AxiosResponse<T>>;

// Implementation
export async function fetchGETRequest<T = any>(
  URL: string,
  body: any = {},
  sendResponse?: boolean
): Promise<T | AxiosResponse<T>> {
  return handleRequest<T>(axios.get, URL, {}, sendResponse);
}

export async function fetchPOSTRequest<T = any>(
  URL: string,
  body?: any,
  sendResponse?: false
): Promise<T>;

export async function fetchPOSTRequest<T = any>(
  URL: string,
  body: any,
  sendResponse: true
): Promise<AxiosResponse<T>>;

export async function fetchPOSTRequest<T = any>(
  URL: string,
  body: any = {},
  sendResponse?: boolean
): Promise<T | AxiosResponse<T>> {
  return handleRequest<T>(
    axios.post,
    URL,
    replaceNullWithEmptyString(body),
    sendResponse
  );
}

export async function fetchPUTRequest<T = any>(
  URL: string,
  body?: any,
  sendResponse?: false
): Promise<T>;

export async function fetchPUTRequest<T = any>(
  URL: string,
  body: any,
  sendResponse: true
): Promise<AxiosResponse<T>>;

export async function fetchPUTRequest<T = any>(
  URL: string,
  body: any = {},
  sendResponse?: boolean
): Promise<T | AxiosResponse<T>> {
  return handleRequest<T>(
    axios.put,
    URL,
    replaceNullWithEmptyString(body),
    sendResponse
  );
}

export async function fetchDELETERequest<T = any>(
  URL: string,
  body?: any,
  sendResponse?: false
): Promise<T>;

export async function fetchDELETERequest<T = any>(
  URL: string,
  body: any,
  sendResponse: true
): Promise<AxiosResponse<T>>;

export async function fetchDELETERequest<T = any>(
  URL: string,
  body: any = {},
  sendResponse?: boolean
): Promise<T | AxiosResponse<T>> {
  return handleRequest<T>(axios.delete, URL, body, sendResponse);
}
